import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Users, Eye, Search, Trash2, ChevronRight, Building2, Phone, UserPlus, GitBranch } from 'lucide-react';
import { toast } from 'sonner';
import { tierLabel } from '@/utils/tierLabels';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function CustomersList() {
  const { API, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [salesUsers, setSalesUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || 'all');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, customer: null });
  const [assignDialog, setAssignDialog] = useState({ open: false, customer: null, selectedSales: '' });
  const [loading, setLoading] = useState(true);

  // Helper function to check if user has any of the allowed roles
  const hasRole = (allowedRoles) => {
    const userRoles = user?.roles || (user?.role ? [user.role] : []);
    return allowedRoles.some(role => userRoles.includes(role));
  };

  const canCreate = hasRole(['admin', 'onboarding', 'sales', 'enterprise_sales', 'lco_sales']);
  const canDelete = hasRole(['admin']);
  const canAssign = hasRole(['admin', 'onboarding']);
  const canChangeTier = hasRole(['admin', 'accounts']);
  const [tierDialog, setTierDialog] = useState({ open: false, customer: null });

  const fetchCustomers = async () => {
    try {
      let url = `${API}/customers`;
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (typeFilter !== 'all') params.append('customer_type', typeFilter);
      if (statusFilter !== 'all') params.append('workflow_status', statusFilter);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
    // Fetch sales users for assignment dropdown
    if (canAssign) {
      fetchSalesUsers();
    }
  }, [API, typeFilter, statusFilter]);

  // Sync filter state with URL ?type= changes (sidebar navigation between Customers/Parent Partners/LCO)
  useEffect(() => {
    const urlType = searchParams.get('type') || 'all';
    const urlStatus = searchParams.get('status') || 'all';
    if (urlType !== typeFilter) setTypeFilter(urlType);
    if (urlStatus !== statusFilter) setStatusFilter(urlStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchSalesUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      // Filter users who have sales, admin, or onboarding role
      const salesEligible = response.data.filter(u => {
        const roles = u.roles || (u.role ? [u.role] : []);
        return roles.some(r => ['sales', 'enterprise_sales', 'lco_sales', 'admin', 'onboarding'].includes(r));
      });
      setSalesUsers(salesEligible);
    } catch (error) {
      console.error('Failed to fetch sales users');
    }
  };

  const handleSearch = () => {
    fetchCustomers();
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (typeFilter !== 'all') params.set('type', typeFilter);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    setSearchParams(params);
  };

  const handleDelete = async () => {
    if (!deleteDialog.customer) return;
    try {
      await axios.delete(`${API}/customers/${deleteDialog.customer.customer_id}`);
      toast.success('Customer deleted successfully');
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to delete customer');
    } finally {
      setDeleteDialog({ open: false, customer: null });
    }
  };

  const handleAssignCustomer = async () => {
    if (!assignDialog.customer || !assignDialog.selectedSales) {
      toast.error('Please select a sales team member');
      return;
    }
    try {
      await axios.post(`${API}/customers/${assignDialog.customer.customer_id}/assign`, {
        sales_email: assignDialog.selectedSales
      });
      toast.success('Customer assigned successfully');
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign customer');
    } finally {
      setAssignDialog({ open: false, customer: null, selectedSales: '' });
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      onboarding: 'bg-slate-100 text-slate-700',
      packages_added: 'bg-blue-100 text-blue-700',
      documents_uploaded: 'bg-indigo-100 text-indigo-700',
      approval_pending: 'bg-amber-100 text-amber-700',
      approved: 'bg-lime-100 text-lime-700',
      po_pending: 'bg-orange-100 text-orange-700',
      po_raised: 'bg-yellow-100 text-yellow-700',
      po_confirmed: 'bg-cyan-100 text-cyan-700',
      delivery_pending: 'bg-violet-100 text-violet-700',
      delivered: 'bg-sky-100 text-sky-700',
      active: 'bg-emerald-100 text-emerald-700'
    };
    return statusColors[status] || 'bg-slate-100 text-slate-700';
  };

  const getTypeBadge = (type) => {
    const typeColors = {
      partner: 'bg-sky-100 text-sky-700',
      corporate: 'bg-emerald-100 text-emerald-700',
      leased_line: 'bg-violet-100 text-violet-700',
      bulk_bandwidth: 'bg-amber-100 text-amber-700'
    };
    return typeColors[type] || 'bg-slate-100 text-slate-700';
  };

  // Mobile Card Component
  const CustomerCard = ({ customer }) => (
    <div 
      className="bg-white border rounded-lg p-4 space-y-3 active:bg-slate-50"
      onClick={() => navigate(`/customers/${customer.customer_id}`)}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-900 truncate">
            <a href={`/customers/${customer.customer_id}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/customers/${customer.customer_id}`); }} className="text-indigo-600 hover:text-indigo-800 hover:underline">
              {customer.company_name}
            </a>
          </p>
          <p className="text-xs text-slate-500 font-mono">{customer.customer_code}</p>
        </div>
        <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
      </div>
      
      <div className="flex flex-wrap gap-2">
        <Badge className={getTypeBadge(customer.customer_type)}>
          {tierLabel(customer.customer_type)}
        </Badge>
        <Badge className={getStatusBadge(customer.workflow_status)}>
          {customer.workflow_status?.replace('_', ' ')}
        </Badge>
      </div>
      
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-1 text-slate-600">
          <Building2 className="w-3.5 h-3.5" />
          <span className="truncate">{customer.city}, {customer.state}</span>
        </div>
        <span className="font-medium text-slate-900">
          ₹{customer.total_monthly_charges?.toLocaleString() || 0}
        </span>
      </div>
      
      <div className="flex items-center gap-2 text-sm text-slate-600">
        <Phone className="w-3.5 h-3.5" />
        <span className="truncate">{customer.contact_person} • {customer.contact_phone}</span>
      </div>
      
      {canDelete && (
        <div className="pt-2 border-t">
          <Button
            size="sm"
            variant="ghost"
            className="text-red-600 hover:text-red-700 hover:bg-red-50 w-full"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteDialog({ open: true, customer });
            }}
          >
            <Trash2 className="h-4 w-4 mr-2" /> Delete
          </Button>
        </div>
      )}
    </div>
  );

  // Page title/subtitle adapts to the current filter (sidebar-driven view)
  const viewMeta = (() => {
    if (typeFilter === 'partner') return { title: 'Master Distributors', subtitle: 'Top-tier partners who manage their own Sub Distributors and LCOs', cardTitle: 'Master Distributors' };
    if (typeFilter === 'iptv_distributor') return { title: 'Sub Distributors', subtitle: 'Mid-tier distributors who resell through their own LCOs', cardTitle: 'Sub Distributors' };
    if (typeFilter === 'lco') return { title: 'LCOs', subtitle: 'Last-mile Local Cable Operators', cardTitle: 'LCOs' };
    if (typeFilter === 'corporate') return { title: 'Corporate Customers', subtitle: 'Manage corporate accounts', cardTitle: 'Corporate Customers' };
    if (typeFilter === 'leased_line') return { title: 'Leased Line Customers', subtitle: 'Manage leased line accounts', cardTitle: 'Leased Line Customers' };
    return { title: 'Customers', subtitle: 'Manage all customers', cardTitle: 'All Customers' };
  })();

  return (
    <div className="space-y-4 lg:space-y-6" data-testid="customers-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl lg:text-3xl font-semibold text-slate-900 tracking-tight">{viewMeta.title}</h2>
          <p className="text-sm lg:text-base text-slate-600 mt-1">{viewMeta.subtitle}</p>
        </div>
        {canCreate && (
          <Button 
            onClick={() => navigate('/customers/new')} 
            className="bg-slate-900 hover:bg-slate-800 text-white w-full sm:w-auto"
            data-testid="add-customer-btn"
          >
            <Plus className="mr-2 h-4 w-4" /> New Customer
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="border-b border-slate-100 bg-slate-50/50 pb-4">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">{viewMeta.cardTitle}</CardTitle>
                <CardDescription>{customers.length} {typeFilter === 'lco' ? 'LCOs' : typeFilter === 'partner' ? 'partners' : 'customers'} found</CardDescription>
              </div>
            </div>
            
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by name, code, email, phone, address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            
            {/* Filter Row */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="partner" data-testid="type-filter-option-master-distributor">Master Distributor</SelectItem>
                  <SelectItem value="iptv_distributor" data-testid="type-filter-option-sub-distributor">Sub Distributor</SelectItem>
                  <SelectItem value="lco" data-testid="type-filter-option-lco">LCO</SelectItem>
                  <SelectItem value="corporate">Corporate</SelectItem>
                  <SelectItem value="leased_line">Leased Line</SelectItem>
                  <SelectItem value="bulk_bandwidth">Bulk Bandwidth</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="onboarding">Onboarding</SelectItem>
                  <SelectItem value="approval_pending">Approval Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleSearch} variant="outline" className="w-full sm:w-auto">
                Search
              </Button>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-0">
          {/* Mobile View - Cards */}
          <div className="lg:hidden p-4 space-y-3">
            {loading ? (
              <div className="text-center text-slate-500 py-8">Loading...</div>
            ) : customers.length === 0 ? (
              <div className="text-center text-slate-500 py-8">
                <Users className="mx-auto h-12 w-12 text-slate-300 mb-2" />
                <p>No customers found</p>
              </div>
            ) : (
              customers.map((customer) => (
                <CustomerCard key={customer.customer_id} customer={customer} />
              ))
            )}
          </div>

          {/* Desktop View - Table */}
          <div className="hidden lg:block">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold text-slate-700">Customer ID</TableHead>
                  <TableHead className="font-semibold text-slate-700">Company</TableHead>
                  <TableHead className="font-semibold text-slate-700">Type</TableHead>
                  <TableHead className="font-semibold text-slate-700">Contact</TableHead>
                  <TableHead className="font-semibold text-slate-700">Monthly</TableHead>
                  <TableHead className="font-semibold text-slate-700">Status</TableHead>
                  <TableHead className="font-semibold text-slate-700">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : customers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                      <Users className="mx-auto h-12 w-12 text-slate-300 mb-2" />
                      <p>No customers found</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  customers.map((customer) => (
                    <TableRow key={customer.customer_id} className="hover:bg-slate-50/50">
                      <TableCell className="font-mono text-sm font-medium text-slate-900">
                        {customer.customer_code}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">
                            <a href={`/customers/${customer.customer_id}`} onClick={(e) => { e.preventDefault(); navigate(`/customers/${customer.customer_id}`); }} className="text-indigo-600 hover:text-indigo-800 hover:underline">
                              {customer.company_name}
                            </a>
                          </p>
                          <p className="text-xs text-slate-500">{customer.city}, {customer.state}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getTypeBadge(customer.customer_type)}>
                          {tierLabel(customer.customer_type)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm text-slate-900">{customer.contact_person}</p>
                          <p className="text-xs text-slate-500">{customer.contact_phone}</p>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium text-slate-900">
                        ₹{customer.total_monthly_charges?.toLocaleString() || 0}
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusBadge(customer.workflow_status)}>
                          {customer.workflow_status?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/customers/${customer.customer_id}`)}
                            data-testid={`view-customer-${customer.customer_id}`}
                          >
                            <Eye className="mr-1 h-3 w-3" /> View
                          </Button>
                          {canAssign && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => setAssignDialog({ open: true, customer, selectedSales: customer.created_by || '' })}
                              title="Assign to Sales"
                            >
                              <UserPlus className="h-3 w-3" />
                            </Button>
                          )}
                          {canChangeTier && ['lco', 'iptv_distributor', 'partner'].includes(customer.customer_type) && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-amber-700 hover:text-amber-800 hover:bg-amber-50"
                              onClick={() => setTierDialog({ open: true, customer })}
                              title="Change tier"
                              data-testid={`row-change-tier-${customer.customer_id}`}
                            >
                              <GitBranch className="h-3 w-3" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setDeleteDialog({ open: true, customer })}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Inline Change Tier Dialog */}
      <ChangeTierInline
        state={tierDialog}
        onClose={() => setTierDialog({ open: false, customer: null })}
        onDone={() => { setTierDialog({ open: false, customer: null }); fetchCustomers(); }}
        API={API}
      />

      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Customer</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {deleteDialog.customer?.company_name}? This will also delete all packages and circuits. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Assign Customer Dialog */}
      <Dialog open={assignDialog.open} onOpenChange={(open) => setAssignDialog({ ...assignDialog, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Customer to Sales</DialogTitle>
            <DialogDescription>
              Assign {assignDialog.customer?.company_name} to a sales team member
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Current Owner</Label>
              <p className="text-sm text-slate-600">{assignDialog.customer?.created_by || 'Not assigned'}</p>
            </div>
            <div className="space-y-2">
              <Label>Assign To</Label>
              <Select 
                value={assignDialog.selectedSales} 
                onValueChange={(v) => setAssignDialog({ ...assignDialog, selectedSales: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select sales team member" />
                </SelectTrigger>
                <SelectContent>
                  {salesUsers.map(u => (
                    <SelectItem key={u.email} value={u.email}>
                      {u.full_name} ({u.roles?.join(', ') || u.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialog({ open: false, customer: null, selectedSales: '' })}>
              Cancel
            </Button>
            <Button onClick={handleAssignCustomer} className="bg-slate-900 hover:bg-slate-800">
              Assign Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Inline Change Tier dialog — mirrors the per-customer one on CustomerDetail,
// so admins / accounts can retier straight from the Customers list.
function ChangeTierInline({ state, onClose, onDone, API }) {
  const customer = state.customer;
  const [newType, setNewType] = useState('iptv_distributor');
  const [newParentPartnerId, setNewParentPartnerId] = useState('');
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!state.open || !customer) return;
    // Sensible defaults based on the current tier.
    if (customer.customer_type === 'lco')               setNewType('iptv_distributor');
    else if (customer.customer_type === 'iptv_distributor') setNewType('partner');
    else                                                setNewType('iptv_distributor');
    setNewParentPartnerId(customer.parent_partner_id || '');

    axios.get(`${API}/customers?customer_type=partner`)
      .then((r) => {
        const d = r.data;
        const list = Array.isArray(d) ? d : (d.customers || d.items || []);
        setPartners(list.filter((c) => ['partner', 'bulk_bandwidth'].includes(c.customer_type)));
      })
      .catch(() => toast.error('Could not load Master Distributors'));
  }, [state.open, customer, API]);

  if (!customer) return null;

  const submit = async () => {
    if (newType !== 'partner' && !newParentPartnerId) {
      toast.error('Pick a parent Master Distributor');
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/customers/${customer.customer_id}/change-tier`, {
        new_type: newType,
        new_parent_partner_id: newType === 'partner' ? null : newParentPartnerId,
        migrate_pricing: true,
      });
      toast.success(`${r.data.message}. ${r.data.pricing_rows_migrated || 0} pricing row(s) migrated.`);
      onDone && onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Tier change failed');
    }
    setLoading(false);
  };

  return (
    <Dialog open={state.open} onOpenChange={(open) => !open && onClose && onClose()}>
      <DialogContent className="max-w-md" data-testid="inline-change-tier-dialog">
        <DialogHeader>
          <DialogTitle>Change Tier · {customer.company_name || customer.customer_code}</DialogTitle>
          <DialogDescription>
            Current: <b>{tierLabel(customer.customer_type)}</b>. Wallet, subs and history are preserved.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Change to</Label>
            <Select value={newType} onValueChange={setNewType}>
              <SelectTrigger data-testid="inline-tier-new-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {customer.customer_type !== 'partner' && <SelectItem value="partner">Master Distributor</SelectItem>}
                {customer.customer_type !== 'iptv_distributor' && <SelectItem value="iptv_distributor">Sub Distributor</SelectItem>}
                {customer.customer_type !== 'lco' && <SelectItem value="lco">LCO</SelectItem>}
                {customer.customer_type === 'lco' && <SelectItem value="lco">LCO (re-parent)</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          {newType !== 'partner' && (
            <div>
              <Label>Parent Master Distributor *</Label>
              <Select value={newParentPartnerId} onValueChange={setNewParentPartnerId}>
                <SelectTrigger data-testid="inline-tier-partner"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {partners.map((p) => (
                    <SelectItem key={p.customer_id} value={p.customer_id}>
                      {p.company_name || p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={loading || (newType !== 'partner' && !newParentPartnerId)}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="inline-tier-submit"
          >
            {loading
              ? 'Converting…'
              : `Convert to ${
                  newType === 'partner' ? 'Master Distributor'
                  : newType === 'iptv_distributor' ? 'Sub Distributor'
                  : 'LCO'
                }`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
